import { useMutation } from "@tanstack/react-query";
import { DeleteScheduleApplicationsBulkBody } from "@common/schemas/domains/accounting/scheduleDeleteBulk.schema";
import { ScheduleLifecycleBulkBody } from "@common/schemas/domains/accounting/scheduleLifecycleBulk.schema";
import { useScope } from "@state/scopeContext.lib";
import { extractApiErrorMessage } from "./apiError";
import {
    SUPPRESS_ERROR_TOAST_HEADERS,
    requestScheduleAction,
    scheduleTemplateClient,
} from "./scheduleTemplate.api";

export const SCHEDULE_LIFECYCLE_BULK_FAILED_MESSAGE =
    "Failed to update schedules";
export const SCHEDULE_DELETE_BULK_FAILED_MESSAGE = "Failed to remove schedules";

function isOkResponse<TResponse extends { status: number }>(
    response: TResponse,
): response is Extract<TResponse, { status: 200 }> {
    return response.status === 200;
}

/** The submit hook invalidates once after its last entity group, not per request. */
function useScheduleBulkMutation<
    TBody,
    TResponse extends { status: number; body: unknown },
>(options: {
    request: (request: {
        workspaceId: string;
        body: TBody;
    }) => Promise<TResponse>;
    fallbackMessage: string;
}) {
    const { workspaceId } = useScope();

    return useMutation({
        mutationFn: async (
            body: TBody,
        ): Promise<Extract<TResponse, { status: 200 }>["body"]> => {
            const response = await requestScheduleAction(
                () => options.request({ workspaceId: workspaceId ?? "", body }),
                options.fallbackMessage,
            );
            if (!isOkResponse(response)) {
                throw new Error(
                    extractApiErrorMessage(
                        response.body,
                        options.fallbackMessage,
                    ),
                );
            }
            return response.body;
        },
    });
}

export function useScheduleLifecycleBulkMutation() {
    return useScheduleBulkMutation({
        request: ({
            workspaceId,
            body,
        }: {
            workspaceId: string;
            body: ScheduleLifecycleBulkBody;
        }) =>
            scheduleTemplateClient.lifecycleBulk({
                extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                params: { workspaceId },
                body,
            }),
        fallbackMessage: SCHEDULE_LIFECYCLE_BULK_FAILED_MESSAGE,
    });
}

export function useDeleteScheduleApplicationsBulkMutation() {
    return useScheduleBulkMutation({
        request: ({
            workspaceId,
            body,
        }: {
            workspaceId: string;
            body: DeleteScheduleApplicationsBulkBody;
        }) =>
            scheduleTemplateClient.deleteApplicationsBulk({
                extraHeaders: SUPPRESS_ERROR_TOAST_HEADERS,
                params: { workspaceId },
                body,
            }),
        fallbackMessage: SCHEDULE_DELETE_BULK_FAILED_MESSAGE,
    });
}
